import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import { buildDefaultClientLine, STATUS_MAP } from '../../hooks/useStoreContract';

export default function StoreContractDialogs({ contract }) {
  const {
    generating,
    sending,
    uploading,
    savingStatus,
    sendDialogOpen,
    sendDialogLoading,
    sendStore,
    sendEmail,
    setSendEmail,
    sendOwnerPhone,
    setSendOwnerPhone,
    sendClientName,
    setSendClientName,
    sendStoreName,
    setSendStoreName,
    sendClientLine,
    setSendClientLine,
    sendContractDate,
    setSendContractDate,
    uploadStore,
    uploadFile,
    setUploadFile,
    editStore,
    setEditStore,
    editStatus,
    setEditStatus,
    editNote,
    setEditNote,
    editSignee,
    setEditSignee,
    logStore,
    setLogStore,
    logs,
    closeSendDialog,
    generateContractOnly,
    doSend,
    doUpload,
    saveStatus,
  } = contract;

  return (
    <>
      <Dialog open={!!editStore} onClose={() => setEditStore(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF' }}>
          계약 상태 변경 — {editStore?.name || editStore?.tljNo}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: 12 }}>상태</InputLabel>
            <Select value={editStatus} label="상태" onChange={(e) => setEditStatus(e.target.value)}
              sx={{ fontSize: 13 }}>
              <MenuItem value="none">미발송</MenuItem>
              <MenuItem value="sent">발송됨</MenuItem>
              <MenuItem value="signed">서명 완료</MenuItem>
              <MenuItem value="expired">만료</MenuItem>
            </Select>
          </FormControl>
          {editStatus === 'signed' && (
            <TextField fullWidth size="small" label="서명인 이름" value={editSignee}
              onChange={(e) => setEditSignee(e.target.value)} sx={{ mb: 2, '& input': { fontSize: 13 } }} />
          )}
          <TextField fullWidth size="small" label="메모 (선택)" value={editNote}
            onChange={(e) => setEditNote(e.target.value)} multiline rows={2}
            sx={{ '& textarea': { fontSize: 12 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditStore(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={saveStatus} disabled={savingStatus}
            sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
            {savingStatus ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!uploadStore} onClose={() => contract.setUploadStore(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF' }}>
          계약서 첨부 — {uploadStore?.name || uploadStore?.tljNo}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          {uploadStore?.contractDocUrl && (
            <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
              기존 파일: <a href={uploadStore.contractDocUrl} target="_blank" rel="noreferrer">{uploadStore.contractDocUrl}</a>
              <br />새 파일 업로드 시 덮어씁니다.
            </Alert>
          )}
          <Button variant="outlined" component="label" fullWidth
            startIcon={<UploadFileIcon />}
            sx={{ py: 2, fontSize: 13, borderStyle: 'dashed', borderColor: '#551FFF', color: '#551FFF' }}>
            {uploadFile ? uploadFile.name : 'PDF / 이미지 파일 선택'}
            <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => contract.setUploadStore(null)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={doUpload} disabled={uploading || !uploadFile}
            sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
            {uploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!logStore} onClose={() => setLogStore(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #551FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: '#551FFF' }} />
          계약 이력 — {logStore?.name || logStore?.tljNo}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {logStore && (
            <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2, mt: 1, background: '#f8f7ff' }}>
              <Typography sx={{ fontSize: 12 }}>
                상태: <Chip label={STATUS_MAP[logStore.contractStatus]?.label || '미발송'} size="small"
                  color={STATUS_MAP[logStore.contractStatus]?.color || 'default'} sx={{ fontSize: 10, height: 20, ml: 0.5 }} />
              </Typography>
              {logStore.contractDocUrl && (
                <Button size="small" variant="outlined" href={logStore.contractDocUrl} target="_blank"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 10, mt: 1, borderColor: '#551FFF', color: '#551FFF' }}>
                  계약서 보기
                </Button>
              )}
            </Paper>
          )}
          {logs.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: 'center', color: '#888', fontSize: 13 }}>이력이 없습니다.</Typography>
          ) : (
            logs.map((log, i) => (
              <Box key={log._id} sx={{ display: 'flex', gap: 1.5, py: 1, borderLeft: `2px solid ${i === 0 ? '#551FFF' : '#e5e7eb'}`, pl: 1.5, ml: 0.5 }}>
                <Typography sx={{ fontSize: 12 }}>
                  {log.action === 'uploaded' ? '파일 업로드' :
                   log.action === 'sent' ? '발송' :
                   log.action === 'created' ? '생성' : log.action}
                  {' · '}
                  {new Date(log.createdAt).toLocaleString('ko-KR')}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogStore(null)} sx={{ color: '#888' }}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendDialogOpen} onClose={closeSendDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, borderBottom: '3px solid #1565C0' }}>
          <SendIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle', color: '#1565C0' }} />
          계약서 생성/발송 — {sendStore?.name || sendStore?.tljNo}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          {sendDialogLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>스토어 최신 정보 불러오는 중...</Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mb: 2, fontSize: 11 }}>
            생성 전에 점주 이름·매장명·연락처를 확인하세요. Admin Settings → Contracts에서도 동일하게 관리됩니다.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField fullWidth size="small" label="점주 이름" value={sendClientName}
              onChange={(e) => {
                const v = e.target.value;
                setSendClientName(v);
                if (sendStore) setSendClientLine(buildDefaultClientLine(sendStore, v));
              }}
              sx={{ '& input': { fontSize: 13 } }} />
            <TextField fullWidth size="small" label="Store name" value={sendStoreName}
              onChange={(e) => setSendStoreName(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField fullWidth size="small" label="Client line (계약서 표시 문구)"
              value={sendClientLine} onChange={(e) => setSendClientLine(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }} />
            <TextField size="small" label="계약 날짜" type="date" value={sendContractDate || ''}
              onChange={(e) => setSendContractDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, '& input': { fontSize: 13 } }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField fullWidth size="small" label="수신 이메일" value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }} />
            <TextField fullWidth size="small" label="Owner 전화" value={sendOwnerPhone}
              onChange={(e) => setSendOwnerPhone(e.target.value)}
              sx={{ '& input': { fontSize: 13 } }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSendDialog} sx={{ color: '#888' }}>취소</Button>
          <Button variant="outlined" onClick={generateContractOnly} disabled={generating}
            startIcon={generating ? <CircularProgress size={14} /> : <DescriptionIcon />}
            sx={{ borderColor: '#551FFF', color: '#551FFF' }}>
            {generating ? '생성 중...' : '생성만'}
          </Button>
          <Button variant="contained" onClick={doSend} disabled={sending || !sendEmail}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
            sx={{ background: '#1565C0', '&:hover': { background: '#0d47a1' } }}>
            {sending ? '발송 중...' : '검토 후 발송'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
